import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { packagingDailyNormService } from '../services/api';

function todayISODate() {
  return new Date().toISOString().slice(0, 10);
}

function PackagingDailyNormPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    date: todayISODate(),
    norm_quantity: '',
    comment: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await packagingDailyNormService.getAll({});
      const list = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      setRows(list);
      setError('');
    } catch (e) {
      console.error(e);
      setError(e.response?.status === 403 ? 'Немає доступу до норм пакування' : 'Не вдалося завантажити дані');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ date: todayISODate(), norm_quantity: '', comment: '' });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      date: row.date?.slice(0, 10) || '',
      norm_quantity: row.norm_quantity != null ? String(row.norm_quantity) : '',
      comment: row.comment || '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    const norm = parseFloat(form.norm_quantity);
    if (!form.date) {
      alert('Вкажіть дату');
      return;
    }
    if (!Number.isFinite(norm) || norm < 0.01) {
      alert('Норма має бути числом не менше 0,01');
      return;
    }
    const payload = {
      date: form.date,
      norm_quantity: norm,
      comment: (form.comment || '').trim(),
    };
    try {
      setSaving(true);
      if (editingId) {
        await packagingDailyNormService.update(editingId, payload);
      } else {
        await packagingDailyNormService.create(payload);
      }
      closeDialog();
      load();
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.data &&
        (typeof e.response.data === 'string' ? e.response.data : JSON.stringify(e.response.data, null, 2));
      alert(msg || e.message || 'Помилка збереження');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Видалити норму на ${row.date}?`)) return;
    try {
      await packagingDailyNormService.delete(row.id);
      load();
    } catch (e) {
      console.error(e);
      alert('Не вдалося видалити');
    }
  };

  if (loading && rows.length === 0 && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Норма пакування на день</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Додати норму
        </Button>
      </Box>
      <Typography color="text.secondary" paragraph sx={{ maxWidth: 800 }}>
        Встановлення цільового обсягу пакування на календарний день для обліку та контролю (наприклад, для ролі
        «Менеджер упаковки»). Одиниці виміру — як у складі напівфабрикатів (за домовленістю на підприємстві).
      </Typography>
      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell align="right">Норма</TableCell>
              <TableCell>Коментар</TableCell>
              <TableCell>Оновлено</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary">Немає записів</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.date ? new Date(r.date).toLocaleDateString('uk-UA') : '—'}</TableCell>
                  <TableCell align="right">{r.norm_quantity}</TableCell>
                  <TableCell>{(r.comment || '').trim() || '—'}</TableCell>
                  <TableCell>
                    {(r.updated_by_name || '').trim() || '—'}
                    {r.updated_at
                      ? ` · ${new Date(r.updated_at).toLocaleString('uk-UA', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      : ''}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(r)} aria-label="Редагувати">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(r)} aria-label="Видалити">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Редагувати норму' : 'Нова норма на день'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Дата"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={form.date}
            onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
            disabled={Boolean(editingId)}
            helperText={editingId ? 'Дату не змінюють — при потребі видаліть запис і створіть новий' : undefined}
          />
          <TextField
            label="Норма на день"
            type="number"
            fullWidth
            margin="normal"
            value={form.norm_quantity}
            onChange={(e) => setForm((p) => ({ ...p, norm_quantity: e.target.value }))}
            inputProps={{ min: 0.01, step: 'any' }}
            required
          />
          <TextField
            label="Коментар"
            fullWidth
            margin="normal"
            value={form.comment}
            onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))}
            placeholder="Необовʼязково"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>
            Скасувати
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PackagingDailyNormPage;
